import { DOCUMENT } from '@angular/common';
import { inject, Injectable, REQUEST } from '@angular/core';
import {
  buildCookieString,
  checkIfCookieExists,
  convertCookiesToObject,
  getCookieByName,
  RmaCookieOptions,
  RmaCookieStorage,
  RmaSameSitePolicy,
} from '@rma/generic/core/cookies';
import { PlatformService } from '../platform/platform.service';

@Injectable({
  providedIn: 'root',
})
export class CookiesService {
  private readonly document = inject(DOCUMENT);
  private readonly request = inject(REQUEST, { optional: true });
  private readonly isBrowser = inject(PlatformService).isPlatformBrowser;

  public check(name: string): boolean {
    const cookie = this.getCookieJar();
    return checkIfCookieExists(name, cookie);
  }

  public get(name: string): string {
    if (!this.check(name)) {
      return '';
    }
    const cookie = this.getCookieJar();
    return getCookieByName(name, cookie);
  }

  public getAll(): RmaCookieStorage {
    const cookieString = this.isBrowser ? this.document?.cookie : this.request?.headers.get('cookie');
    return convertCookiesToObject(cookieString);
  }

  public put(name: string, value: string, options: RmaCookieOptions = {}): void {
    if (!this.isBrowser) return;
    const cookieString = buildCookieString(name, value, options);
    this.document.cookie = cookieString;
  }

  public delete(name: string, path?: string, domain?: string, secure?: boolean, sameSite: RmaSameSitePolicy = 'Lax'): void {
    if (!this.isBrowser) {
      console.log(this.isBrowser);
      return;
    }
    const expiresDate = new Date('Thu, 01 Jan 1970 00:00:01 GMT');
    this.put(name, '', {
      expires: expiresDate,
      path,
      domain,
      secure,
      sameSite,
    });
  }

  public deleteAll(path?: string, domain?: string, secure?: boolean, sameSite: RmaSameSitePolicy = 'Lax'): void {
    if (!this.isBrowser) {
      return;
    }
    const cookies = this.getAll();
    Object.keys(cookies).forEach((cookieName) => this.delete(cookieName, path, domain, secure, sameSite));
  }

  private getCookieJar(): string {
    try {
      return this.isBrowser ? this.document?.cookie || '' : this.request?.headers.get('cookie') || '';
    } catch (error) {
      console.error('Error accessing cookies:', error);
      return '';
    }
  }
}
